import { SceneRenderer } from "@cva/renderer";
import {
  clone,
  fitTextScales,
  type MergedPage,
  type SceneDocument,
  scaleTextSize,
  type TextElement,
} from "@cva/shared";
import { flushSync } from "react-dom";
import { createRoot } from "react-dom/client";

/**
 * Text heights of filled series pages. The canvas only measures the open
 * page, so new pages are measured here: the texts are rendered with the
 * regular renderer into a hidden container (same fonts, wrapping and rich
 * text as on the canvas and in exports).
 */
function createMeasurer(doc: SceneDocument) {
  const host = document.createElement("div");
  host.setAttribute("aria-hidden", "true");
  host.style.cssText =
    "position:fixed;left:-100000px;top:0;visibility:hidden;pointer-events:none;overflow:hidden;width:1px;height:1px";
  document.body.appendChild(host);
  const root = createRoot(host);
  const { timeline: _timeline, ...base } = doc;
  return {
    measure(elements: readonly TextElement[]): Map<string, number> {
      const temp: SceneDocument = {
        ...base,
        pages: [
          { id: "measure", background: "#ffffff", children: elements.map((item) => item.id) },
        ],
        elements: Object.fromEntries(
          elements.map((item) => [
            item.id,
            { ...item, x: 0, y: 0, rotation: 0, hidden: false, opacity: 1 },
          ]),
        ),
      };
      flushSync(() => root.render(<SceneRenderer document={temp} clip={false} animate={false} />));
      const heights = new Map<string, number>();
      for (const item of elements) {
        const node = host.querySelector<HTMLElement>(
          `[data-element-id="${CSS.escape(item.id)}"] [data-text-content]`,
        );
        heights.set(item.id, node ? Math.max(1, Math.ceil(node.offsetHeight)) : item.height);
      }
      return heights;
    },
    dispose() {
      root.unmount();
      host.remove();
    },
  };
}

/**
 * Fits the texts that got new content: optionally shrinks their font until
 * they fit the template box height, then sets auto-height boxes to their
 * content (growing downwards like on the canvas). Changes `pages` in place.
 */
export async function fitMergedTexts(
  template: SceneDocument,
  pages: readonly MergedPage[],
  options: { shrink: boolean },
): Promise<{ shrunk: number }> {
  const candidates: { element: TextElement; maxHeight: number }[] = [];
  for (const merged of pages) {
    for (const [oldId, newId] of merged.idMap) {
      const before = template.elements[oldId];
      const after = merged.elements[newId];
      if (before?.type !== "text" || after?.type !== "text" || before.text === after.text) continue;
      candidates.push({ element: after, maxHeight: before.height });
    }
  }
  if (candidates.length === 0) return { shrunk: 0 };
  // Web fonts of the template are usually loaded already; wait for pending ones.
  await document.fonts.ready.catch(() => undefined);
  const measurer = createMeasurer(template);
  try {
    let shrunk = 0;
    if (options.shrink) {
      const byId = new Map(candidates.map((item) => [item.element.id, item.element]));
      const factors = await fitTextScales(
        candidates.map((item) => ({ id: item.element.id, maxHeight: item.maxHeight })),
        (probe) =>
          measurer.measure(
            [...probe].map(([id, factor]) => {
              const copy = clone(byId.get(id)!);
              scaleTextSize(copy, factor);
              return copy;
            }),
          ),
        // Never below half the size: very long values grow the box instead.
        { minFactor: 0.5 },
      );
      for (const [id, factor] of factors) {
        if (factor < 1) {
          scaleTextSize(byId.get(id)!, factor);
          shrunk++;
        }
      }
    }
    const heights = measurer.measure(candidates.map((item) => item.element));
    for (const { element } of candidates) {
      if (!element.autoHeight) continue;
      const height = heights.get(element.id) ?? element.height;
      // Grow downwards, keeping the top edge in place (also when rotated).
      const delta = height - element.height;
      const angle = (element.rotation * Math.PI) / 180;
      element.x += (-Math.sin(angle) * delta) / 2;
      element.y += (Math.cos(angle) * delta) / 2 - delta / 2;
      element.height = height;
    }
    return { shrunk };
  } finally {
    measurer.dispose();
  }
}
